import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, ClipboardCopy, CheckCircle2, AlertTriangle } from "lucide-react";
import { buildMepaText } from "@/components/MepaDialog";
import { DateField, todayRome } from "@/components/DateField";

const input = "w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:border-gold outline-none";
const kmOf = (p) => (p.distanceKm ? Math.round(p.distanceKm * 2) : 0);

export const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success("Dati copiati negli appunti"); }
  catch { toast.error("Copia non riuscita: seleziona il testo e copialo manualmente"); }
};

export const requestText = (r) => buildMepaText(r.mepa, { eventType: r.eventType, eventDate: r.eventDate, eventLocation: r.eventLocation });

function SentView({ result, providers }) {
  const name = (id) => providers.find((p) => p.id === id)?.companyName || "Operatore";
  return (
    <div className="space-y-3" data-testid="quick-request-sent">
      {result.created.length > 0 && <p className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4"/>Richiesta inviata a {result.created.length} {result.created.length === 1 ? "operatore" : "operatori"}. Le risposte arriveranno nella Dashboard. Ecco i dati per la Trattativa Diretta:</p>}
      {result.skipped.map((s) => <p key={s.operatorId} className="text-xs text-amber-700 flex items-start gap-2" data-testid={`quick-request-skipped-${s.operatorId}`}><AlertTriangle className="w-4 h-4 shrink-0"/>{name(s.operatorId)}: {s.reason}</p>)}
      {result.created.map((r) => (
        <details key={r.id} open={result.created.length === 1} className="border border-slate-200 rounded-xl p-3" data-testid={`quick-request-result-${r.operatorId}`}>
          <summary className="cursor-pointer text-sm font-semibold text-navy">{r.operatorName}</summary>
          <pre data-testid="quick-request-text" className="whitespace-pre-wrap text-xs bg-slate-50 border border-slate-200 rounded-xl p-3 mt-2 font-mono text-slate-800 max-h-60 overflow-y-auto">{requestText(r)}</pre>
          <button type="button" data-testid={`quick-request-copy-${r.operatorId}`} onClick={() => copyText(requestText(r))} className="btn-pill-outline w-full justify-center text-xs mt-2"><ClipboardCopy className="w-3.5 h-3.5"/>Copia negli Appunti</button>
        </details>
      ))}
    </div>
  );
}

export function QuickRequestDialog({ providers, open, onOpenChange, onSent }) {
  const { user } = useAuth();
  const [f, setF] = useState({ eventDate: "", eventLocation: "", eventType: "", notes: "" });
  const [km, setKm] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setF((p) => ({ ...p, eventLocation: p.eventLocation || user?.city || "" }));
    setKm(Object.fromEntries(providers.map((p) => [p.id, kmOf(p)])));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!f.eventDate) { toast.error("Scegli la data dell'evento"); return; }
    setBusy(true);
    try {
      const r = await api.post("/mepa/requests/bulk", { operatorIds: providers.map((p) => p.id), distanceKmByOperator: km, ...f, notes: f.notes.trim() || null });
      setResult(r.data);
      if (r.data.created.length) { toast.success("Richiesta inviata"); onSent?.(); }
      else toast.error("Nessuna richiesta inviata");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Controlla i campi della richiesta");
    } finally { setBusy(false); }
  };

  const many = providers.length > 1;
  const today = todayRome();
  const counts = {};
  providers.forEach((p) => (p.availabilityCalendar || []).forEach((d) => { if (d >= today) counts[d] = (counts[d] || 0) + 1; }));
  const visible = providers.filter((p) => !p.calendarHidden).length;
  const allFree = Object.keys(counts).filter((d) => visible > 0 && counts[d] === visible && d !== f.eventDate);
  const someFree = Object.keys(counts).filter((d) => counts[d] < visible && d !== f.eventDate);
  const freeOn = (p) => (p.calendarHidden ? null : (p.availabilityCalendar || []).includes(f.eventDate));
  const legend = (<>
    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-500"/>{many ? "Liberi tutti gli operatori con calendario pubblico" : "Giorno libero per l'operatore"}</div>
    {many && <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"/>Libero solo qualche operatore</div>}
    {providers.some((p) => p.calendarHidden) && <div className="text-slate-400">Alcuni operatori non pubblicano il calendario.</div>}
  </>);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="quick-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-navy">Richiesta Trattativa Rapida</DialogTitle>
          <DialogDescription>{many ? `La stessa richiesta parte a ${providers.length} operatori MePA` : `Chiedi a ${providers[0]?.companyName} se è disponibile`}: ricevono un'email e ti rispondono dalla Dashboard.</DialogDescription>
        </DialogHeader>
        {result ? <SentView result={result} providers={providers}/> : (
          <form onSubmit={submit} className="space-y-3">
            <label className="block text-xs font-semibold text-slate-600">Data evento
              <DateField testId="quick-request-date" value={f.eventDate} min={today} allFree={allFree} someFree={someFree} legend={legend} onChange={(v) => set("eventDate", v)} className={`${input} mt-1`}/></label>
            <input required minLength={2} data-testid="quick-request-location" placeholder="Luogo dell'evento" value={f.eventLocation} onChange={(e) => set("eventLocation", e.target.value)} className={input}/>
            <input required minLength={2} data-testid="quick-request-type" placeholder="Tipo evento (es. Festa patronale)" value={f.eventType} onChange={(e) => set("eventType", e.target.value)} className={input}/>
            <div className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid="quick-request-operators">
              <div className="text-xs font-semibold text-slate-600">Trasferta A/R (km) per operatore</div>
              {providers.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate flex-1">{p.companyName}</span>
                  {f.eventDate && (() => { const s = freeOn(p); return <span data-testid={`quick-request-free-${p.id}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${s === true ? "bg-emerald-100 text-emerald-800" : s === false ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{s === true ? "Libero" : s === false ? "Non indicato libero" : "Calendario non pubblico"}</span>; })()}
                  <input type="number" min={0} data-testid={`quick-request-km-${p.id}`} value={km[p.id] ?? 0} onChange={(e) => setKm((k) => ({ ...k, [p.id]: parseInt(e.target.value) || 0 }))} className="w-24 border border-slate-300 rounded-lg px-2 py-1 text-right text-sm"/>
                </div>
              ))}
            </div>
            <textarea data-testid="quick-request-notes" rows={3} maxLength={2000} placeholder="Note per gli operatori (facoltativo): orari, durata, esigenze tecniche…" value={f.notes} onChange={(e) => set("notes", e.target.value)} className={input}/>
            <button data-testid="quick-request-submit" disabled={busy} className="btn-pill-gold w-full justify-center disabled:opacity-50"><Send className="w-4 h-4"/>{busy ? "Invio…" : many ? `Invia a ${providers.length} operatori` : "Invia richiesta"}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function QuickRequestButton({ provider }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" data-testid={`quick-request-${provider.id}`} onClick={() => setOpen(true)} className="btn-pill-gold w-full justify-center text-sm"><Send className="w-4 h-4"/>Richiedi disponibilità</button>
      <QuickRequestDialog providers={[provider]} open={open} onOpenChange={setOpen}/>
    </>
  );
}
